import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Copy, Loader2, Minus, Plus, Trash2, X } from "lucide-react";
import axios from "axios";
import bizApi, { apiError } from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { VEHICLES } from "@/lib/data";
import RoutePreviewMap from "@/components/RoutePreviewMap";
import SumUpPayment from "@/components/booking/SumUpPayment";
import { btnPrimary, btnSecondary, btnGhost, cardCls, formatChf, inputCls, labelCls } from "./ui";
import { DateField, TimeField, GuestPicker } from "./fields";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export const OPTION_LABELS = {
  fr: { baby_seat: "Siège bébé", booster: "Réhausseur", wheelchair: "Fauteuil roulant / assistance", pet: "Animal", ski_equipment: "Matériel de ski", golf_equipment: "Matériel de golf", large_luggage: "Gros bagages", meet_greet: "Meet & Greet", vip: "VIP", discretion: "Discrétion requise", signboard: "Pancarte", lobby_pickup: "Prise en charge au lobby", whatsapp_only: "WhatsApp uniquement", do_not_call: "Ne pas appeler le passager", extra_luggage_assistance: "Assistance bagages renforcée" },
  en: { baby_seat: "Baby seat", booster: "Booster", wheelchair: "Wheelchair / mobility assistance", pet: "Pet", ski_equipment: "Ski equipment", golf_equipment: "Golf equipment", large_luggage: "Large luggage", meet_greet: "Meet & Greet", vip: "VIP", discretion: "Discretion required", signboard: "Signboard", lobby_pickup: "Lobby pickup", whatsapp_only: "WhatsApp only", do_not_call: "Do not call passenger", extra_luggage_assistance: "Extra luggage assistance" },
  de: { baby_seat: "Babyseat", booster: "Sitzerhöhung", wheelchair: "Rollstuhl / Assistenz", pet: "Tier", ski_equipment: "Skiausrüstung", golf_equipment: "Golfausrüstung", large_luggage: "Grosses Gepäck", meet_greet: "Meet & Greet", vip: "VIP", discretion: "Diskretion erforderlich", signboard: "Namensschild", lobby_pickup: "Abholung in der Lobby", whatsapp_only: "Nur WhatsApp", do_not_call: "Gast nicht anrufen", extra_luggage_assistance: "Zusätzliche Gepäckhilfe" },
};

const PAYER_METHODS = { partner: ["business_invoice", "card"], guest: ["payment_link", "card", "cash"] };

const emptyGuest = () => ({ first_name: "", last_name: "", phone: "", email: "", language: "", save_as_guest: false });
const emptyLeg = (type) => ({
  pickup: "", dropoff: "", date: "", time: "", stops: [],
  flight: type === "airport" ? { number: "", airport: "", terminal: "" } : null,
  train: null, private_aviation: type === "private_aviation" ? { tail_number: "", fbo: "", handler: "", airport: "", eta: "", contact: "" } : null,
  duration_hours: type === "hourly" ? 4 : null, schedule: [], flexible_destination: false,
});

const NEEDS_GUESTS = !1;
const NO_GUEST_TYPES = ["luggage", "delivery"];
const QUOTED_TYPES = ["group", "event", "shuttle", "recurring", "custom"];

export default function MissionBuilder({ org, type, initial, trainMode, setTrainMode, onBack, onDone }) {
  const { t, lang } = useBiz();
  const commissionsOn = Boolean(org?.commercial?.commission_enabled);

  const [draft, setDraft] = useState(() => ({
    type,
    guests: initial?.guests?.length ? initial.guests.map((g) => ({ ...emptyGuest(), ...g })) : (NO_GUEST_TYPES.includes(type) ? [] : [emptyGuest()]),
    passengers_count: initial?.passengers_count || 1,
    luggage_count: initial?.luggage_count || 0,
    legs: initial?.legs?.length ? initial.legs.map((l) => ({ ...emptyLeg(type), ...l })) : [emptyLeg(type)],
    vehicles: initial?.vehicles?.length ? initial.vehicles : [{ category: type === "luggage" ? "luggage_van" : "business", count: 1 }],
    options: initial?.options || [],
    signboard_text: initial?.signboard_text || "",
    driver_language: initial?.driver_language || "",
    payer: initial?.payer?.method ? initial.payer : { type: "partner", method: "business_invoice" },
    references: initial?.references || {},
    notes: initial?.notes || {},
    show_price_to_guest: initial?.show_price_to_guest || false,
    recurrence: type === "recurring" ? { weekday: 0, time: "07:30", from: "", to: "" } : null,
    shuttle: type === "shuttle" ? { frequency_min: 30, window_start: "18:00", window_end: "23:00" } : null,
    cargo: type === "luggage" ? { bag_count: 0, bag_types: "", oversized: false, collect_from: "", deliver_to: "", contact_phone: "", deliver_before: "", instructions: "" } : null,
    delivery: type === "delivery" ? { description: "", size: "", weight: "", fragile: false, sender: "", recipient: "", deadline: "" } : null,
    custom_request: type === "custom" ? { description: "" } : null,
  }));
  const [directory, setDirectory] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  // paiement carte partenaire — widget SumUp intégré (aucune redirection)
  const [cardReady, setCardReady] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState(null);
  const [payDone, setPayDone] = useState(false);
  const payFnRef = useRef(null);
  const registerPay = useCallback((fn) => { payFnRef.current = fn; }, []);

  const retryPartnerCard = async () => {
    // checkout refusé/échoué = mort côté SumUp → on en crée un frais (widget)
    if (!done?.id || payBusy) return;
    setPayBusy(true);
    setPayError(null);
    try {
      const r = await bizApi.post(`/missions/${done.id}/payment-link`, { widget: true });
      setCardReady(false);
      setDone((d) => ({ ...d, payment: { ...(d.payment || {}), checkout_id: r.data.checkout_id, status: "open" } }));
    } catch {
      setPayError(t("pay.failed"));
    } finally {
      setPayBusy(false);
    }
  };

  const submitPartnerCard = async () => {
    if (!done?.payment?.checkout_id || !payFnRef.current || payBusy) return;
    setPayBusy(true);
    setPayError(null);
    try {
      const res = await payFnRef.current();
      if (res?.error) { setPayError(t("pay.failed")); return; }
      // vérification SERVEUR — le callback du widget seul ne prouve jamais le paiement.
      // 3DS : le checkout peut passer PAID quelques secondes après → on vérifie en boucle.
      let status = "pending";
      for (let i = 0; i < 10 && status === "pending"; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const v = await bizApi.post(`/missions/${done.id}/verify-payment`);
        status = v.data.status;
        // eslint-disable-next-line no-await-in-loop
        if (status === "pending") await new Promise((r) => { setTimeout(r, 3000); });
      }
      if (status === "paid") {
        setPayDone(true);
        setDone((d) => ({ ...d, payment: { ...d.payment, status: "paid" } }));
      } else if (status === "failed") {
        setPayError(t("pay.failed"));
      } else {
        setPayError(t("pay.pending_check"));
      }
    } catch {
      setPayError(t("pay.failed"));
    } finally {
      setPayBusy(false);
    }
  };
  const [copied, setCopied] = useState(false);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setLeg = (i, patch) => setDraft((d) => ({ ...d, legs: d.legs.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const setGuest = (i, patch) => setDraft((d) => ({ ...d, guests: d.guests.map((g, j) => (j === i ? { ...g, ...patch } : g)) }));

  useEffect(() => {
    bizApi.get("/guests").then((r) => setDirectory(r.data)).catch(() => {});
  }, []);

  // estimation live (types tarifables, 1er trajet)
  useEffect(() => {
    const leg = draft.legs[0];
    if (QUOTED_TYPES.includes(type) || !leg?.pickup || (!leg.dropoff && type !== "hourly")) {
      setEstimate(null);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      axios.post(`${process.env.REACT_APP_BACKEND_URL || ""}/api/estimate`, {
        pickup: leg.pickup, dropoff: leg.dropoff || undefined,
        trip_type: type === "hourly" ? "hourly" : "one_way",
        duration_hours: leg.duration_hours || 2,
      }, { signal: ctrl.signal }).then((r) => setEstimate(r.data)).catch(() => setEstimate(null));
    }, 600);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [draft.legs, type]);

  const estimatedTotal = useMemo(() => {
    if (!estimate?.categories) return null;
    let total = 0;
    for (const v of draft.vehicles) {
      const cat = estimate.categories.find((c) => c.id === (v.category === "luggage_van" ? "van" : v.category));
      if (!cat) return null;
      total += cat.price_chf * (v.count || 1);
    }
    return total;
  }, [estimate, draft.vehicles]);

  // --- sélection véhicules par cartes (comme la réservation publique) ---
  const vehicleCatalog = type === "luggage" ? ["luggage_van"] : ["business", "first", "van", "standard"];
  const catPrice = (id) =>
    estimate?.categories?.find((c) => c.id === (id === "luggage_van" ? "van" : id))?.price_chf ?? null;
  const vehicleOf = (id) => draft.vehicles.find((v) => v.category === id);
  const toggleVehicle = (id) => {
    const existing = vehicleOf(id);
    if (!existing) set({ vehicles: [...draft.vehicles, { category: id, count: 1 }] });
    else if (draft.vehicles.length > 1) set({ vehicles: draft.vehicles.filter((v) => v.category !== id) });
  };
  const bumpCount = (id, delta) =>
    set({ vehicles: draft.vehicles.map((v) => (v.category === id ? { ...v, count: Math.min(20, Math.max(1, (v.count || 1) + delta)) } : v)) });

  const commissionRate = org?.commercial?.rate;
  const commissionEst = commissionsOn && commissionRate && estimatedTotal
    ? (estimatedTotal * commissionRate) / 100 : null;

  const valid = useMemo(() => {
    if (type === "custom") return (draft.custom_request?.description || "").trim().length >= 5;
    const leg = draft.legs[0];
    if (!leg?.pickup || !leg.date || (!leg.time && type !== "recurring")) return false;
    if (!NO_GUEST_TYPES.includes(type) && !draft.guests.some((g) => g.first_name && g.last_name)) return false;
    if (!["hourly", "custom"].includes(type) && !leg.dropoff) return false;
    if (type === "luggage" && !draft.cargo?.bag_count) return false;
    if (type === "delivery" && !draft.delivery?.description) return false;
    if (type === "recurring" && (!draft.recurrence?.from || !draft.recurrence?.to)) return false;
    return true;
  }, [draft, type]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        ...draft,
        type: trainMode ? "train" : type,
        lang,
        origin_url: window.location.origin,
        // purge : jamais de guests vides ni de legs vides (sinon 422 Pydantic)
        guests: draft.guests.filter((g) => (g.first_name || "").trim() && (g.last_name || "").trim()),
        legs: type === "custom" ? [] : draft.legs.filter((l) => (l.pickup || "").trim().length >= 2).map((l) => ({
          ...l,
          train: trainMode ? (l.train || { number: "", station: l.pickup, scheduled: l.time, platform: "" }) : l.train,
          flight: trainMode ? null : l.flight,
        })),
      };
      const r = await bizApi.post("/missions", payload);
      const mission = r.data;
      // carte partenaire : le formulaire SumUp s'affiche directement dans l'écran de confirmation
      setDone(mission);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(Array.isArray(detail) ? t("common.invalid") : (typeof detail === "string" ? detail : t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  const bookReturn = () => {
    const leg = draft.legs[0];
    setDone(null);
    setDraft((d) => ({ ...d, legs: [{ ...emptyLeg(d.type), pickup: leg.dropoff || "", dropoff: leg.pickup || "", date: "", time: "" }] }));
  };

  if (done) {
    const leg = done.legs?.[0] || {};
    const guest = done.guests?.[0];
    return (
      <div className="mx-auto max-w-xl" data-testid="mission-confirmed">
        <div className={`${cardCls} p-8 text-center`}>
          <p className="text-2xl font-bold tracking-tight text-ink">{t("mb.confirmed_title")}</p>
          <p className="mt-1 text-sm text-slate">{t("mb.confirmed_sub")}</p>
          <div className="mt-6 rounded-2xl bg-cloud p-5 text-left">
            <p className="font-mono text-sm font-bold text-miles" data-testid="confirmed-number">{done.number}</p>
            {guest && <p className="mt-2 text-sm font-semibold text-ink">{guest.first_name} {guest.last_name}</p>}
            {leg.pickup && <p className="mt-1 text-sm text-slate">{leg.pickup}{leg.dropoff ? ` → ${leg.dropoff}` : ""}</p>}
            {(leg.date || leg.time) && <p className="text-sm text-slate">{leg.date}{leg.time ? ` — ${leg.time}` : ""}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {done.guests?.some((g) => g.email) && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">Guest notified</span>}
              {leg.flight?.number && <span className="rounded-full bg-miles-ice px-2.5 py-1 text-[11px] font-medium text-miles">{t("mb.flight_tracked")}</span>}
              {done.pricing?.status === "to_quote" && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">{t("mb.to_quote")}</span>}
            </div>
            {done.payer?.method === "payment_link" && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-left" data-testid="confirmed-payment-link">
                <p className="text-xs font-semibold text-amber-800">{t("pay.pending")}</p>
                {done.payment?.url && (
                  <button type="button" data-testid="confirmed-copy-link"
                    onClick={() => { navigator.clipboard?.writeText(done.payment.url); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-miles ring-1 ring-miles/25 transition-colors hover:bg-miles-ice">
                    <Copy className="h-3.5 w-3.5" /> {copied ? t("pay.copied") : t("pay.copy")}
                  </button>
                )}
                <p className="mt-2 text-[11px] leading-relaxed text-amber-700">{t("pay.link_sent")}</p>
              </div>
            )}
            {done.payer?.type === "partner" && done.payer?.method === "card" && (
              payDone ? (
                <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="mission-paid-ok">{t("pay.paid_ok")}</p>
              ) : done.payment?.checkout_id ? (
                <div className="mt-3 rounded-xl border border-ink/10 bg-white p-4 text-left" data-testid="mission-card-widget">
                  <p className="text-xs font-semibold text-ink">{t("pay.widget_title")}</p>
                  <div className="mt-2">
                    <SumUpPayment checkoutId={done.payment.checkout_id} registerPay={registerPay}
                      onCompleteChange={setCardReady} email={done.booker?.email} />
                  </div>
                  {payError && <p className="mt-2 text-xs font-medium text-red-600" data-testid="mission-pay-error">{payError}</p>}
                  <button type="button" onClick={payError ? retryPartnerCard : submitPartnerCard} disabled={(!cardReady && !payError) || payBusy}
                    data-testid="mission-pay-button"
                    className={`${btnPrimary} mt-3 w-full justify-center disabled:opacity-50`}>
                    {payBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {payBusy ? t("pay.processing") : payError ? t("pay.retry") : t("pay.now_amount").replace("{amount}", formatChf(done.pricing?.amount || 0))}
                  </button>
                </div>
              ) : (
                <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="mission-card-unavailable">{t("pay.widget_unavailable")}</p>
              )
            )}
            {done.payer?.method === "cash" && (
              <p className="mt-3 rounded-xl bg-cloud px-3 py-2 text-xs font-medium text-slate" data-testid="confirmed-cash">{t("pay.cash_planned")}</p>
            )}
            {done.payer?.method === "business_invoice" && (
              <p className="mt-3 rounded-xl bg-cloud px-3 py-2 text-xs font-medium text-slate" data-testid="confirmed-invoice">{t("pay.on_invoice")}</p>
            )}
            {commissionEst && done.pricing?.amount && (
              <p className="mt-3 text-xs text-slate">{t("mb.commission_est")} : <span className="font-semibold text-ink">{formatChf(done.pricing.amount * commissionRate / 100)}</span></p>
            )}
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            <button type="button" onClick={() => onDone(done.id)} className={btnPrimary} data-testid="confirmed-track">{t("mb.track")}</button>
            {!NO_GUEST_TYPES.includes(type) && <button type="button" onClick={bookReturn} className={btnSecondary} data-testid="confirmed-return">{t("mb.add_return")}</button>}
            <button type="button" onClick={() => { setDone(null); onBack(); }} className={btnGhost} data-testid="confirmed-new">{t("mb.new_one")}</button>
          </div>
        </div>
      </div>
    );
  }

  const leg = draft.legs[0];
  const showGuests = !NO_GUEST_TYPES.includes(type) && type !== "custom";
  const showFlights = type === "airport" && !trainMode;

  return (
    <div data-testid="mission-builder" className="mx-auto max-w-5xl">
      <button type="button" onClick={onBack} className={btnGhost} data-testid="builder-back">← {t("mb.back")}</button>
      <div className="mt-2 flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-ink">{t(`type.${trainMode ? "airport" : type}`)}</h1>
        {type === "airport" && setTrainMode && (
          <div className="flex rounded-xl border border-ink/10 bg-white p-1" data-testid="airport-train-toggle">
            {[t("mb.mode.flight"), t("mb.mode.train")].map((m, i) => (
              <button key={m} type="button" onClick={() => setTrainMode(i === 1)} data-testid={`mode-${i === 1 ? "train" : "flight"}`}
                className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${(i === 1) === trainMode ? "bg-miles text-white" : "text-slate"}`}>
                {m}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          {/* -------- BLOC QUI -------- */}
          {showGuests && (
            <section className={`${cardCls} p-5`} data-testid="block-who">
              <h2 className="text-sm font-bold text-ink">{t("mb.who")}</h2>
              <div className="mt-3 space-y-3">
                {draft.guests.map((g, i) => (
                  <div key={i} className="rounded-xl border border-ink/[0.07] p-3" data-testid={`guest-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <GuestPicker guests={directory} testId={`guest-${i}-directory`} className="h-9 text-xs"
                        onPick={(found) => setGuest(i, { ...emptyGuest(), ...found, guest_id: found.id, save_as_guest: false })} />
                      {draft.guests.length > 1 && (
                        <button type="button" onClick={() => set({ guests: draft.guests.filter((_, j) => j !== i) })}
                          className="text-slate hover:text-red-500" data-testid={`guest-${i}-remove`}><X className="h-4 w-4" /></button>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <input className={`${inputCls} h-10`} placeholder={t("guests.first_name")} value={g.first_name}
                        onChange={(e) => setGuest(i, { first_name: e.target.value })} data-testid={`guest-${i}-first`} />
                      <input className={`${inputCls} h-10`} placeholder={t("guests.last_name")} value={g.last_name}
                        onChange={(e) => setGuest(i, { last_name: e.target.value })} data-testid={`guest-${i}-last`} />
                      <input className={`${inputCls} h-10`} placeholder={t("guests.phone")} value={g.phone || ""}
                        onChange={(e) => setGuest(i, { phone: e.target.value })} data-testid={`guest-${i}-phone`} />
                      <input className={`${inputCls} h-10`} placeholder={t("guests.email")} value={g.email || ""}
                        onChange={(e) => setGuest(i, { email: e.target.value })} data-testid={`guest-${i}-email`} />
                    </div>
                    {!g.guest_id && (
                      <label className="mt-2 flex items-center gap-2 text-xs text-slate">
                        <input type="checkbox" checked={g.save_as_guest} onChange={(e) => setGuest(i, { save_as_guest: e.target.checked })}
                          data-testid={`guest-${i}-save`} className="h-3.5 w-3.5 rounded accent-miles" />
                        {t("mb.save_guest")}
                      </label>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => set({ guests: [...draft.guests, emptyGuest()] })}
                  className={btnGhost} data-testid="guest-add"><Plus className="h-3.5 w-3.5" /> {t("mb.add_guest")}</button>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <label className={labelCls}>{t("mb.passengers")}</label>
                    <input type="number" min="1" className={inputCls} value={draft.passengers_count}
                      onChange={(e) => set({ passengers_count: Number(e.target.value) })} data-testid="passengers-count" />
                  </div>
                  <div>
                    <label className={labelCls}>{t("mb.luggage")}</label>
                    <input type="number" min="0" className={inputCls} value={draft.luggage_count}
                      onChange={(e) => set({ luggage_count: Number(e.target.value) })} data-testid="luggage-count" />
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* -------- BLOC BAGAGES / LIVRAISON / CUSTOM -------- */}
          {type === "luggage" && (
            <section className={`${cardCls} p-5`} data-testid="block-cargo">
              <h2 className="text-sm font-bold text-ink">{t("type.luggage")}</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div><label className={labelCls}>{t("mb.bag_count")}</label>
                  <input type="number" min="1" className={inputCls} value={draft.cargo.bag_count}
                    onChange={(e) => set({ cargo: { ...draft.cargo, bag_count: Number(e.target.value) } })} data-testid="cargo-count" /></div>
                <div><label className={labelCls}>{t("mb.bag_types")}</label>
                  <input className={inputCls} value={draft.cargo.bag_types}
                    onChange={(e) => set({ cargo: { ...draft.cargo, bag_types: e.target.value } })} data-testid="cargo-types" /></div>
                <div><label className={labelCls}>{t("mb.collect_from")}</label>
                  <AddressAutocomplete value={draft.cargo.collect_from} testId="cargo-from" inputClass={inputCls}
                    onChange={(v) => set({ cargo: { ...draft.cargo, collect_from: v } })} /></div>
                <div><label className={labelCls}>{t("mb.deliver_to")}</label>
                  <AddressAutocomplete value={draft.cargo.deliver_to} testId="cargo-to" inputClass={inputCls}
                    onChange={(v) => set({ cargo: { ...draft.cargo, deliver_to: v } })} /></div>
                <div><label className={labelCls}>{t("guests.phone")}</label>
                  <input className={inputCls} value={draft.cargo.contact_phone}
                    onChange={(e) => set({ cargo: { ...draft.cargo, contact_phone: e.target.value } })} data-testid="cargo-phone" /></div>
                <div><label className={labelCls}>{t("mb.deliver_before")}</label>
                  <TimeField value={draft.cargo.deliver_before} testId="cargo-deadline"
                    onChange={(v) => set({ cargo: { ...draft.cargo, deliver_before: v } })} /></div>
              </div>
              <label className="mt-2 flex items-center gap-2 text-xs text-slate">
                <input type="checkbox" checked={draft.cargo.oversized} className="h-3.5 w-3.5 rounded accent-miles"
                  onChange={(e) => set({ cargo: { ...draft.cargo, oversized: e.target.checked } })} data-testid="cargo-oversized" />
                {t("mb.oversized")}
              </label>
            </section>
          )}
          {type === "delivery" && (
            <section className={`${cardCls} p-5`} data-testid="block-delivery">
              <h2 className="text-sm font-bold text-ink">{t("type.delivery")}</h2>
              <div className="mt-3 space-y-2">
                <input className={inputCls} placeholder={t("mb.item_description")} value={draft.delivery.description}
                  onChange={(e) => set({ delivery: { ...draft.delivery, description: e.target.value } })} data-testid="delivery-desc" />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls} placeholder={t("mb.sender")} value={draft.delivery.sender}
                    onChange={(e) => set({ delivery: { ...draft.delivery, sender: e.target.value } })} data-testid="delivery-sender" />
                  <input className={inputCls} placeholder={t("mb.recipient")} value={draft.delivery.recipient}
                    onChange={(e) => set({ delivery: { ...draft.delivery, recipient: e.target.value } })} data-testid="delivery-recipient" />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate">
                  <input type="checkbox" checked={draft.delivery.fragile} className="h-3.5 w-3.5 rounded accent-miles"
                    onChange={(e) => set({ delivery: { ...draft.delivery, fragile: e.target.checked } })} data-testid="delivery-fragile" />
                  {t("mb.fragile")}
                </label>
              </div>
            </section>
          )}
          {type === "custom" && (
            <section className={`${cardCls} p-5`} data-testid="block-custom">
              <h2 className="text-sm font-bold text-ink">{t("type.custom")}</h2>
              <textarea className={`${inputCls} mt-3 h-32 py-3`} placeholder={t("mb.custom_desc")}
                value={draft.custom_request.description}
                onChange={(e) => set({ custom_request: { description: e.target.value } })} data-testid="custom-desc" />
              <p className="mt-2 text-xs text-slate">{t("mb.custom_hint")}</p>
            </section>
          )}

          {/* -------- BLOC OÙ / QUAND -------- */}
          {type !== "custom" && (
            <section className={`${cardCls} p-5`} data-testid="block-where">
              <h2 className="text-sm font-bold text-ink">{t("mb.where")} · {t("mb.when")}</h2>
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>{t("mb.pickup")}</label>
                    <AddressAutocomplete value={leg.pickup} testId="leg-pickup" inputClass={inputCls}
                      onChange={(v) => setLeg(0, { pickup: v })} />
                  </div>
                  <div>
                    <label className={labelCls}>{type === "hourly" ? t("mb.flexible_dest") : t("mb.dropoff")}</label>
                    <AddressAutocomplete value={leg.dropoff || ""} testId="leg-dropoff" inputClass={`${inputCls} ${type === "hourly" ? "opacity-50" : ""}`}
                      placeholder={type === "hourly" ? t("mb.schedule_hint") : ""}
                      onChange={(v) => type !== "hourly" && setLeg(0, { dropoff: v })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className={labelCls}>{t("mb.date")}</label>
                    <DateField value={leg.date} testId="leg-date"
                      onChange={(v) => setLeg(0, { date: v })} /></div>
                  <div><label className={labelCls}>{t("mb.time")}</label>
                    <TimeField value={leg.time} testId="leg-time"
                      onChange={(v) => setLeg(0, { time: v })} /></div>
                </div>

                {showFlights && (
                  <div className="grid grid-cols-2 gap-2 rounded-xl bg-miles-ice/50 p-3" data-testid="flight-fields">
                    <div><label className={labelCls}>{t("mb.flight_number")}</label>
                      <input className={`${inputCls} h-10 uppercase`} value={leg.flight?.number || ""}
                        onChange={(e) => setLeg(0, { flight: { ...leg.flight, number: e.target.value.toUpperCase() } })} data-testid="flight-number" /></div>
                    <div><label className={labelCls}>{t("mb.terminal")}</label>
                      <input className={`${inputCls} h-10`} value={leg.flight?.terminal || ""}
                        onChange={(e) => setLeg(0, { flight: { ...leg.flight, terminal: e.target.value } })} data-testid="flight-terminal" /></div>
                    {leg.flight?.number && <p className="col-span-2 text-[11px] font-medium text-miles">{t("mb.flight_tracked")}</p>}
                  </div>
                )}
                {trainMode && (
                  <div className="grid grid-cols-3 gap-2 rounded-xl bg-miles-ice/50 p-3" data-testid="train-fields">
                    <div><label className={labelCls}>{t("mb.train_number")}</label>
                      <input className={`${inputCls} h-10`} value={leg.train?.number || ""}
                        onChange={(e) => setLeg(0, { train: { ...leg.train, number: e.target.value } })} data-testid="train-number" /></div>
                    <div><label className={labelCls}>{t("mb.station")}</label>
                      <input className={`${inputCls} h-10`} value={leg.train?.station || ""}
                        onChange={(e) => setLeg(0, { train: { ...leg.train, station: e.target.value } })} data-testid="train-station" /></div>
                    <div><label className={labelCls}>{t("mb.platform")}</label>
                      <input className={`${inputCls} h-10`} value={leg.train?.platform || ""}
                        onChange={(e) => setLeg(0, { train: { ...leg.train, platform: e.target.value } })} data-testid="train-platform" /></div>
                  </div>
                )}
                {type === "private_aviation" && (
                  <div className="grid grid-cols-2 gap-2 rounded-xl bg-miles-ice/50 p-3" data-testid="aviation-fields">
                    {[["tail_number", t("mb.tail_number")], ["fbo", t("mb.fbo")], ["handler", t("mb.handler")], ["airport", "Airport"], ["eta", t("mb.eta")], ["contact", t("mb.aviation_contact")]].map(([k, lab]) => (
                      <div key={k}><label className={labelCls}>{lab}</label>
                        <input className={`${inputCls} h-10`} value={leg.private_aviation?.[k] || ""}
                          onChange={(e) => setLeg(0, { private_aviation: { ...leg.private_aviation, [k]: e.target.value } })} data-testid={`aviation-${k}`} /></div>
                    ))}
                  </div>
                )}
                {type === "hourly" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>{t("mb.duration")}</label>
                      <select className={inputCls} value={leg.duration_hours}
                        onChange={(e) => setLeg(0, { duration_hours: Number(e.target.value) })} data-testid="duration">
                        {[2, 4, 8, 12].map((h) => <option key={h} value={h}>{h} {t("mb.hours")}</option>)}
                      </select></div>
                  </div>
                )}

                {(type === "multi_stop" || type === "transfer") && (
                  <div className="space-y-2 pt-1" data-testid="stops-editor">
                    {leg.stops.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-xl border border-ink/[0.07] p-2.5" data-testid={`stop-${i}`}>
                        <span className="text-xs font-bold text-slate">{t("mb.stop")} {i + 1}</span>
                        <AddressAutocomplete value={s.address || ""} testId={`stop-${i}-address`} inputClass={`${inputCls} h-9 flex-1`}
                          placeholder={t("places.address")}
                          onChange={(v) => setLeg(0, { stops: leg.stops.map((x, j) => (j === i ? { ...x, address: v } : x)) })} />
                        <TimeField value={s.time || ""} className="h-9 w-28" testId={`stop-${i}-time`}
                          onChange={(v) => setLeg(0, { stops: leg.stops.map((x, j) => (j === i ? { ...x, time: v } : x)) })} />
                        <button type="button" disabled={i === 0}
                          onClick={() => { const c = [...leg.stops];[c[i - 1], c[i]] = [c[i], c[i - 1]]; setLeg(0, { stops: c }); }}
                          className="text-slate hover:text-ink disabled:opacity-30" data-testid={`stop-${i}-up`}><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button type="button" disabled={i === leg.stops.length - 1}
                          onClick={() => { const c = [...leg.stops];[c[i + 1], c[i]] = [c[i], c[i + 1]]; setLeg(0, { stops: c }); }}
                          className="text-slate hover:text-ink disabled:opacity-30" data-testid={`stop-${i}-down`}><ArrowDown className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => setLeg(0, { stops: leg.stops.filter((_, j) => j !== i) })}
                          className="text-slate hover:text-red-500" data-testid={`stop-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setLeg(0, { stops: [...leg.stops, { address: "", time: "" }] })}
                      className={btnGhost} data-testid="stop-add"><Plus className="h-3.5 w-3.5" /> {t("mb.add_stop")}</button>
                  </div>
                )}

                {type === "shuttle" && (
                  <div className="grid grid-cols-3 gap-2 rounded-xl bg-miles-ice/50 p-3" data-testid="shuttle-fields">
                    <div><label className={labelCls}>{t("mb.shuttle_frequency")}</label>
                      <input type="number" min="5" className={`${inputCls} h-10`} value={draft.shuttle.frequency_min}
                        onChange={(e) => set({ shuttle: { ...draft.shuttle, frequency_min: Number(e.target.value) } })} data-testid="shuttle-freq" /></div>
                    <div><label className={labelCls}>{t("mb.shuttle_window")} — {t("mb.from")}</label>
                      <TimeField value={draft.shuttle.window_start} className="h-10" testId="shuttle-start"
                        onChange={(v) => set({ shuttle: { ...draft.shuttle, window_start: v } })} /></div>
                    <div><label className={labelCls}>{t("mb.to")}</label>
                      <TimeField value={draft.shuttle.window_end} className="h-10" testId="shuttle-end"
                        onChange={(v) => set({ shuttle: { ...draft.shuttle, window_end: v } })} /></div>
                  </div>
                )}

                {type === "recurring" && (
                  <div className="grid grid-cols-4 gap-2 rounded-xl bg-miles-ice/50 p-3" data-testid="recurrence-fields">
                    <div><label className={labelCls}>{t("mb.every")}</label>
                      <select className={`${inputCls} h-10`} value={draft.recurrence.weekday}
                        onChange={(e) => set({ recurrence: { ...draft.recurrence, weekday: Number(e.target.value) } })} data-testid="rec-weekday">
                        {[0, 1, 2, 3, 4, 5, 6].map((d) => <option key={d} value={d}>{t(`mb.weekday.${d}`)}</option>)}
                      </select></div>
                    <div><label className={labelCls}>{t("mb.time")}</label>
                      <TimeField value={draft.recurrence.time} className="h-10" testId="rec-time"
                        onChange={(v) => set({ recurrence: { ...draft.recurrence, time: v } })} /></div>
                    <div><label className={labelCls}>{t("mb.from")}</label>
                      <DateField value={draft.recurrence.from} className="h-10" testId="rec-from"
                        onChange={(v) => set({ recurrence: { ...draft.recurrence, from: v } })} /></div>
                    <div><label className={labelCls}>{t("mb.to")}</label>
                      <DateField value={draft.recurrence.to} className="h-10" testId="rec-to"
                        onChange={(v) => set({ recurrence: { ...draft.recurrence, to: v } })} /></div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* -------- BLOC COMMENT -------- */}
          {type !== "custom" && (
            <section className={`${cardCls} p-5`} data-testid="block-how">
              <h2 className="text-sm font-bold text-ink">{t("mb.how")}</h2>
              <div className="mt-3 grid gap-2.5 sm:grid-cols-3">
                {vehicleCatalog.map((id) => {
                  const sel = vehicleOf(id);
                  const price = catPrice(id);
                  const img = VEHICLES[id === "luggage_van" ? "van" : id]?.image;
                  return (
                    <div key={id} data-testid={`vehicle-card-${id}`}
                      className={`overflow-hidden rounded-2xl border bg-white transition-[border-color,box-shadow] duration-150 ${
                        sel ? "border-miles shadow-[0_14px_35px_-24px_rgba(36,87,255,0.45)]" : "border-ink/10 hover:border-miles/35"}`}>
                      <button type="button" onClick={() => toggleVehicle(id)} className="block w-full text-left"
                        data-testid={`vehicle-card-${id}-toggle`} aria-pressed={Boolean(sel)}>
                        <div className="flex h-24 items-center justify-center bg-gradient-to-b from-white to-miles-ice/60">
                          {img && <img src={img} alt={t(`mb.vehicle.${id}`)} className="h-full w-full object-contain p-3" loading="lazy" />}
                        </div>
                        <div className="px-3.5 pb-3 pt-2.5">
                          <p className="text-sm font-semibold text-ink">{t(`mb.vehicle.${id}`)}</p>
                          <p className="mt-0.5 font-mono text-[13px] font-semibold text-miles" data-testid={`vehicle-card-${id}-price`}>
                            {price != null ? formatChf(price) : "—"}
                          </p>
                        </div>
                      </button>
                      {sel && vehicleCatalog.length > 0 && (
                        <div className="flex items-center justify-between border-t border-ink/[0.06] px-3 py-1.5">
                          <span className="text-[11px] text-slate">{t("mb.count")}</span>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => bumpCount(id, -1)} disabled={sel.count <= 1}
                              className="flex h-6 w-6 items-center justify-center rounded-lg border border-ink/10 text-slate transition-colors hover:border-miles/40 hover:text-miles disabled:opacity-30"
                              data-testid={`vehicle-card-${id}-minus`}><Minus className="h-3 w-3" /></button>
                            <span className="w-5 text-center font-mono text-sm font-semibold text-ink" data-testid={`vehicle-card-${id}-count`}>{sel.count}</span>
                            <button type="button" onClick={() => bumpCount(id, 1)}
                              className="flex h-6 w-6 items-center justify-center rounded-lg border border-ink/10 text-slate transition-colors hover:border-miles/40 hover:text-miles"
                              data-testid={`vehicle-card-${id}-plus`}><Plus className="h-3 w-3" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* -------- BLOC INSTRUCTIONS -------- */}
          {type !== "custom" && (
            <section className={`${cardCls} p-5`} data-testid="block-instructions">
              <h2 className="text-sm font-bold text-ink">{t("mb.instructions")}</h2>
              <div className="mt-3 space-y-2">
                <input className={inputCls} placeholder={t("mb.driver_note")} value={draft.notes.driver || ""}
                  onChange={(e) => set({ notes: { ...draft.notes, driver: e.target.value } })} data-testid="note-driver" />
                <input className={inputCls} placeholder={t("mb.internal_note")} value={draft.notes.internal || ""}
                  onChange={(e) => set({ notes: { ...draft.notes, internal: e.target.value } })} data-testid="note-internal" />
              </div>
            </section>
          )}

          {/* -------- BLOC QUI PAIE -------- */}
          <section className={`${cardCls} p-5`} data-testid="block-payment">
            <h2 className="text-sm font-bold text-ink">{t("mb.payment")}</h2>
            <div className="mt-3 flex gap-2">
              {["partner", "guest"].map((p) => (
                <button key={p} type="button" data-testid={`payer-${p}`}
                  onClick={() => set({ payer: { type: p, method: PAYER_METHODS[p][0] } })}
                  className={`flex-1 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-colors ${
                    draft.payer.type === p ? "border-miles bg-miles-ice/60 text-miles" : "border-ink/10 bg-white text-slate hover:border-miles/35"}`}>
                  {t(`mb.payer.${p}`)}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs font-medium text-slate">{t("mb.how_pay")}</p>
            <div className="mt-2 space-y-2">
              {PAYER_METHODS[draft.payer.type].map((m) => {
                const key = m === "card" && draft.payer.type === "guest" ? "card_guest" : m;
                return (
                <label key={m} data-testid={`method-${m}`}
                  className={`flex cursor-pointer items-start gap-2.5 rounded-xl border p-3 transition-colors ${
                    draft.payer.method === m ? "border-miles bg-miles-ice/40" : "border-ink/10 bg-white hover:border-miles/30"}`}>
                  <input type="radio" name="pay_method" checked={draft.payer.method === m}
                    onChange={() => set({ payer: { ...draft.payer, method: m } })} className="mt-0.5 h-4 w-4 accent-miles" />
                  <span>
                    <span className="block text-sm font-medium text-ink">{t(`mb.method.${key}`)}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-slate">{t(`mb.method.${key}_h`)}</span>
                  </span>
                </label>
              );})}
            </div>
          </section>
        </div>

        {/* -------- RÉSUMÉ STICKY -------- */}
        <aside className="lg:sticky lg:top-40 h-fit" data-testid="mission-review">
          {estimate?.route && (
            <div className="mb-3" data-testid="mission-route-map">
              <RoutePreviewMap geometry={estimate.route} />
            </div>
          )}
          <div className={`${cardCls} p-5`}>
            <h2 className="text-sm font-bold text-ink">{t("mb.review")}</h2>
            <div className="mt-3 space-y-1.5 text-[13px]">
              <p className="font-semibold text-ink">{t(`type.${trainMode ? "airport" : type}`)}</p>
              {leg?.pickup && <p className="text-slate">{leg.pickup} → {leg.dropoff || "—"}</p>}
              {leg?.date && <p className="text-slate">{leg.date} · {leg.time}</p>}
              {draft.guests.filter((g) => g.first_name).map((g, i) => (
                <p key={i} className="text-ink">{g.first_name} {g.last_name}</p>
              ))}
              {draft.cargo?.bag_count > 0 && <p className="text-ink">{draft.cargo.bag_count} × {t("type.luggage").toLowerCase()}</p>}
              <p className="text-slate">{draft.vehicles.map((v) => `${v.count}× ${t(`mb.vehicle.${v.category}`)}`).join(" · ")}</p>
            </div>
            <div className="mt-4 border-t border-ink/[0.06] pt-3">
              {estimatedTotal !== null ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-slate">{t("mb.price_est")}</span>
                  <span className="font-mono text-lg font-bold text-ink" data-testid="review-price">{formatChf(estimatedTotal)}</span>
                </div>
              ) : (
                <p className="text-xs text-amber-700" data-testid="review-to-quote">{t("mb.to_quote")}</p>
              )}
              {commissionEst !== null && (
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-xs text-slate">{t("mb.commission_est")} ({commissionRate} %)</span>
                  <span className="font-mono text-sm font-semibold text-emerald-600" data-testid="review-commission">{formatChf(commissionEst)}</span>
                </div>
              )}
            </div>
            {error && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600" data-testid="builder-error">{error}</p>}
            <button type="button" onClick={submit} disabled={!valid || busy}
              className={`${btnPrimary} mt-4 w-full`} data-testid="builder-confirm">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("mb.creating")}</> : t("mb.confirm")}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
